import { Button, Field, Input, useI18n } from '@upweo/ui';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { ApiClientError, setAccessToken, setActiveShop } from '../lib/api-client.js';
import * as api from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';

/** Mirrors the server's slugify so the preview matches what will be created. */
const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

export const SignupPage = ({ onSignIn }: { onSignIn: () => void }): React.JSX.Element => {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [storeName, setStoreName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The address follows the store name until the merchant edits it themselves,
  // after which typing the name must not silently overwrite their choice.
  useEffect(() => {
    if (!slugEdited) {
      setSlug(slugify(storeName));
    }
  }, [storeName, slugEdited]);

  const availability = useQuery({
    queryKey: ['slug', slug],
    queryFn: ({ signal }) => api.checkSlug(slug, signal),
    enabled: slug.length > 1,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    void (async () => {
      try {
        const result = await api.signup({
          email,
          password,
          storeName,
          storeSlug: slug || undefined,
          currencyCode: 'USD',
          countryCode: 'US',
        });
        setAccessToken(result.accessToken);
        setActiveShop(result.user.memberships[0]?.tenantSlug ?? null);
        // Adopt the session the server just issued rather than sending the
        // merchant to a login screen they have no reason to see.
        await refresh();
      } catch (err) {
        setError(
          err instanceof ApiClientError
            ? (err.error.fieldErrors?.map((f) => f.message).join(', ') ?? err.error.message)
            : 'Something went wrong. Try again.',
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  const taken = availability.data && !availability.data.available;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-xl border bg-card p-8 shadow-md">
        <h1 className="text-2xl font-semibold tracking-tight">{t('auth.createStore')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Free 14-day trial. Your storefront goes live immediately.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Field label={t('auth.storeName')} htmlFor="storeName">
            <Input
              id="storeName"
              required
              value={storeName}
              onChange={(e) => {
                setStoreName(e.target.value);
              }}
              placeholder="Northwind Supply"
            />
          </Field>

          <Field
            label={t('auth.storeAddress')}
            htmlFor="slug"
            {...(taken && availability.data?.suggestion
              ? { error: `Taken. Try "${availability.data.suggestion}".` }
              : { hint: slug ? `${slug}.upweo.local` : 'Chosen from your store name' })}
          >
            <Input
              id="slug"
              value={slug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(slugify(e.target.value));
              }}
            />
          </Field>

          <Field label={t('common.email')} htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </Field>

          <Field label={t('common.password')} htmlFor="password" hint={t('auth.passwordHint')}>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          <Button type="submit" disabled={busy || taken} className="w-full">
            {busy ? 'Creating your store…' : 'Create store'}
          </Button>
        </form>

        <p className="mt-6 border-t pt-4 text-sm text-muted-foreground">
          Already have a store?{' '}
          <button
            type="button"
            onClick={onSignIn}
            className="text-primary underline-offset-4 hover:underline"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};
